import React, { useState, useEffect } from "react";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import LessonItem from "./LessonItem";
import { useModuleContext } from "../../Context/ModuleContext";
import {
  Button,
  Modal,
  Tooltip,
  OverlayTrigger,
  Card,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import { useHttpClient } from "../../hooks/http-hook";
import { FaEyeSlash, FaEye, FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { IoIosRemoveCircle } from "react-icons/io";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faLightbulb,
  faQuestionCircle,
  faCloudUploadAlt,
  faFilePdf,
  faFileWord,
} from "@fortawesome/free-solid-svg-icons";

import DeleteConfirmationModal from "../DeleteConfirmationModal";
import DragHandle from "../DragHandle";
import VisibilityButton from "../VisibilityButton";
import EditableTitle from "../EditableTitle";
import Notification from "../Notification/Notification";
import { useTranslation } from "react-i18next"; // Importer useTranslation

const SectionItem = ({ section, onAddLesson }) => {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const { sendRequest } = useHttpClient();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: section.id });

  const [notificationParams, setNotificationParams] = useState({
    show: false,
    message: t("notification_default_message"),
    bg: "danger",
  });
  const [modalOpenDelete, setModalOpenDelete] = useState(false);
  const [modalOpenAddLesson, setModalOpenAddLesson] = useState(false);
  const [visible, setVisible] = useState(section.is_visible);
  const [editIndex, setEditIndex] = useState(null);
  const [editTitle, setEditTitle] = useState(section.title);
  const [collapsed, setCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { refreshModule } = useModuleContext();

  const handleDoubleClick = () => {
    setEditIndex(section.id);
    setEditTitle(section.title);
  };

  const handleTitleChange = (e) => {
    setEditTitle(e.target.value);
  };

  const handleBlur = async (newTitle) => {
    if (newTitle.trim() === "") {
      setNotificationParams({
        show: true,
        message: t("empty_title_error"),
        bg: "danger",
      });
      return;
    }
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}sections/${section.id}`,
        "PATCH",
        JSON.stringify({ title: newTitle }),
        { "Content-Type": "application/json" }
      );

      if (!response.ok) {
        throw new Error(response.message || t("update_title_error"));
      }

      console.log(t("title_updated_success"));
      setEditIndex(null);
      refreshModule();
    } catch (err) {
      console.error(t("update_title_error"), err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleBlur(editTitle);
    }
  };

  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    padding: "12px",
    backgroundColor: "#e3f2fd",
    border: "1px solid #90caf9",
    borderRadius: "6px",
    marginBottom: "8px",
  };

  const lessonTypes = [
    { type: t("theory"), icon: faBook },
    { type: t("quiz"), icon: faQuestionCircle },
    { type: t("upload"), icon: faCloudUploadAlt },
    { type: t("pdf"), icon: faFilePdf },
    { type: t("word"), icon: faFileWord },
  ];

  const handleModalDeleteClose = () => {
    setModalOpenDelete(false);
  };

  const handleModalAddLessonClose = () => {
    setModalOpenAddLesson(false);
  };

  const renderToolTip = (props, message) => (
    <Tooltip id="button-tooltip" {...props}>
      {message}
    </Tooltip>
  );

  const handleSetVisible = async (newValue) => {
    setVisible(newValue);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}sections/${section.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ is_visible: newValue }),
        }
      );

      if (!response.ok) {
        throw new Error(t("update_visibility_error"));
      }

      refreshModule();
      console.log(t("visibility_updated_success"));
    } catch (err) {
      console.error(t("update_visibility_error"), err);
    }
  };

  const handleAddLesson = async (type) => {
    handleModalAddLessonClose();
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}lessons`,
        "POST",
        JSON.stringify({
          title: t("new_lesson"),
          section: section.id,
          module: section.module,
          type: type,
        }),
        { "Content-Type": "application/json" }
      );

      const newLesson = response.lesson;
      if (newLesson) {
        onAddLesson(section.id, {
          id: newLesson._id,
          title: newLesson.title,
          type: newLesson.type,
        });
        console.log(t("lesson_added_success"), newLesson);
        refreshModule();
      } else {
        console.error(t("add_lesson_error"), response);
      }
    } catch (err) {
      console.error(t("add_lesson_error"), err);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}sections/${sectionId}`,
        "PATCH",
        JSON.stringify({ is_deleted: true, is_visible: false }),
        { "Content-Type": "application/json" }
      );

      if (!response.ok) {
        throw new Error(response.message || t("delete_section_error"));
      }

      console.log(t("section_deleted_success"));
      refreshModule();
    } catch (err) {
      console.error(t("delete_section_error"), err);
    }
  };

  return (
    <>
      <div
        ref={setNodeRef}
        className="p-3 bg-light bg-gradient border rounded-3 shadow-sm mb-2"
        style={{
          transform: transform ? CSS.Transform.toString(transform) : undefined,
          transition,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2 align-items-center">
            {isHovered && (
              <div
                style={{
                  opacity: isHovered ? 1 : 0,
                  transition: "opacity 0.2s ease",
                }}
              >
                <DragHandle listeners={listeners} attributes={attributes} />
              </div>
            )}
            <EditableTitle
              title={editTitle}
              onEdit={handleDoubleClick}
              onSave={handleBlur}
              onChange={handleTitleChange}
              itemType="section"
            />
            <button
              className="btn btn-outline-secondary btn-sm ms-2"
              onClick={() => setCollapsed(!collapsed)}
              data-drag-handle="false"
            >
              {collapsed ? <FaChevronDown /> : <FaChevronUp />}
            </button>
          </div>
          <div className="d-flex gap-2 align-items-center justify-content-end">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => setModalOpenAddLesson(true)}
              data-drag-handle="false"
            >
              {t("add_lesson")}
            </button>
            <OverlayTrigger
              placement="top"
              overlay={renderToolTip(section, t("delete"))}
            >
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setModalOpenDelete(true)}
                id={`tooltip-delete-${section.id}`}
              >
                <IoIosRemoveCircle />
              </button>
            </OverlayTrigger>
            <VisibilityButton
              isVisible={visible}
              onClick={() => handleSetVisible(!visible)}
              itemType="section"
            />
          </div>
        </div>

        {!collapsed && section.lessons?.length > 0 ? (
          <SortableContext
            items={section.lessons.map((lesson) => lesson.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="ps-4 mt-3">
              {section.lessons.map((lesson) => (
                <LessonItem key={lesson.id} lesson={lesson} />
              ))}
            </div>
          </SortableContext>
        ) : (
          !collapsed && (
            <p className="text-muted fst-italic small mt-2">
              {t("no_lessons_yet")}
            </p>
          )
        )}
      </div>

      <DeleteConfirmationModal
        item={section}
        itemType="section"
        show={modalOpenDelete}
        onClose={handleModalDeleteClose}
        onDelete={handleDeleteSection}
      />

      <Modal
        show={modalOpenAddLesson}
        onHide={handleModalAddLessonClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("select_lesson_type")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Row>
              {lessonTypes.map((lesson, index) => (
                <Col key={index} md={4} className="mb-3">
                  <Card
                    style={{ cursor: "pointer", textAlign: "center" }}
                    onClick={() => handleAddLesson(lesson.type)}
                  >
                    <Card.Body>
                      <FontAwesomeIcon
                        icon={lesson.icon}
                        size="3x"
                        style={{ marginBottom: "10px" }}
                      />
                      <Card.Title>{lesson.type}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SectionItem;
